import React, { useState, useEffect, useRef } from "react";
import { CircularProgress, Box, Typography } from "@mui/material";

/* ───────── helpers ───────── */
/** Cache the *factory* (fn that accepts React and returns a component),
 *  not the bound component, so each instance can pass a scoped React. */
const compiledCache = new Map();

/* ───────── keyboard‑scoping infrastructure (singleton) ───────── */
let __patched = false;
let __activeId = null; // the only dynamic component that should receive keys
let __registeringForId = null; // set while a component mounts/effects run
const __roots = new Map(); // id -> root HTMLElement
const KEY_EVENTS = new Set(["keydown", "keypress", "keyup"]);

// per-target store: target -> (type -> (origFn -> [{ ownerId, wrapped, options }]))
const __store = new Map();

const __isEditable = (el) => {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.isContentEditable) return true;
  if (el.closest?.('[contenteditable="true"]')) return true;
  return false;
};

function __getTypeStore(target, type) {
  let t = __store.get(target);
  if (!t) {
    t = new Map();
    __store.set(target, t);
  }
  let s = t.get(type);
  if (!s) {
    s = new Map();
    t.set(type, s);
  }
  return s;
}

function __patchTargetOnce(target) {
  if (!target || target.__dcKeyScoped) return;
  const origAdd = target.addEventListener.bind(target);
  const origRem = target.removeEventListener.bind(target);

  target.addEventListener = function (type, listener, options) {
    if (
      KEY_EVENTS.has(type) &&
      typeof listener === "function" &&
      __registeringForId
    ) {
      const ownerId = __registeringForId;
      const wrapped = function (e) {
        if (__activeId === ownerId) {
          return listener.call(this, e);
        }
        // Not active → do nothing; importantly, we DO NOT preventDefault.
      };
      const store = __getTypeStore(target, type);
      const arr = store.get(listener) || [];
      arr.push({ ownerId, wrapped, options });
      store.set(listener, arr);
      return origAdd(type, wrapped, options);
    }
    return origAdd(type, listener, options);
  };

  target.removeEventListener = function (type, listener, options) {
    if (KEY_EVENTS.has(type) && typeof listener === "function") {
      const store = __getTypeStore(target, type);
      const arr = store.get(listener);
      if (arr && arr.length) {
        // Remove all wrapped variants for this original listener
        arr.forEach(({ wrapped, options: opts }) =>
          origRem(type, wrapped, opts)
        );
        store.delete(listener);
        return;
      }
    }
    return origRem(type, listener, options);
  };

  target.__dcKeyScoped = true;
}

function __installKeyScopingOnce() {
  if (__patched) return;
  // Patch common global targets for keyboard events
  [window, document, document.body].forEach(__patchTargetOnce);

  // Deactivate on focus into an editable outside the active root
  document.addEventListener(
    "focusin",
    (e) => {
      if (!__activeId) return;
      const root = __roots.get(__activeId);
      if (root && !root.contains(e.target) && __isEditable(e.target)) {
        __activeId = null;
      }
    },
    true
  );

  // Deactivate when clicking/tapping outside the active root
  const outside = (e) => {
    if (!__activeId) return;
    const root = __roots.get(__activeId);
    if (root && !root.contains(e.target)) {
      __activeId = null;
    }
  };
  document.addEventListener("pointerdown", outside, true);
  document.addEventListener("touchstart", outside, true);

  // ESC anywhere deactivates
  window.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Escape") __activeId = null;
    },
    true
  );

  __patched = true;
}

/** Small error boundary so runtime errors don't lock us in a spinner */
class DCErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    this.props.onError?.(error, info);
  }
  render() {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}

/**
 * Dynamically compiles & mounts a component.
 * Now with:
 *   • watchdog timeout (maxWaitMs) → onFail("timeout")
 *   • error boundary → onFail("runtime-error: …")
 *   • compile errors → onFail("compile-error: …")
 *   • optional admin fallback UI instead of hiding
 *   • keyboard event scoping (only one component active at a time)
 */
export default function DynamicComponent({
  source,
  props = {},
  heightHint = 400,
  maxWaitMs = 3500,
  onReady,
  onFail,
  isAdmin = false,
}) {
  const [Comp, setComp] = useState(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | failed
  const rootRef = useRef(null);
  const timeoutRef = useRef(null);
  const notifiedFail = useRef(false);
  const idRef = useRef(`dc-${Math.random().toString(36).slice(2)}`);

  /* whenever the *code string* changes → reset                     */
  useEffect(() => {
    setComp(null);
    setStatus("idle");
    setVisible(false);
    notifiedFail.current = false;
  }, [source]);

  // Install global key scoping once
  useEffect(() => {
    __installKeyScopingOnce();
  }, []);

  // Register this instance root so we can check "outside" interactions
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    __roots.set(idRef.current, el);
    return () => {
      __roots.delete(idRef.current);
      if (__activeId === idRef.current) __activeId = null;
    };
  }, []);

  /* Observe when we're close to viewport                           */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /* Fallback: if IO never fires (rare with content-visibility/RAF throttling),
     arm a gentle auto-start so we don't spin forever. */
  useEffect(() => {
    if (!source) return;
    const t = setTimeout(
      () => setVisible((v) => v || true),
      Math.min(800, Math.floor(maxWaitMs / 2))
    );
    return () => clearTimeout(t);
  }, [source, maxWaitMs]);

  /* Compile lazily & off-main-scroll; watchdog for "stuck spinner"  */
  useEffect(() => {
    if (!source || !visible || Comp || status === "failed") return;

    setStatus("loading");

    const fail = (msg) => {
      if (notifiedFail.current) return;
      notifiedFail.current = true;
      setStatus("failed");
      onFail?.(msg);
    };

    const compile = () => {
      try {
        // Preprocess source: replace old dictionary API with our server proxy
        // (filters for English-only definitions, returns 404 if not found)
        const processedSource = source.replace(
          /https:\/\/api\.dictionaryapi\.dev\/api\/v2\/entries\/en\//g,
          "https://tweets-ai.onrender.com/api/dictionary/"
        );

        // 1) Get/compile factory for this source (factory(React) -> Component)
        let factory = compiledCache.get(processedSource);
        if (!factory) {
          factory = new Function("React", processedSource);
          compiledCache.set(processedSource, factory);
        }
        // 2) Build a React "view" that tags event listener registration
        const scopedReact = new Proxy(React, {
          get(target, prop, receiver) {
            if (prop === "useEffect") {
              return (eff, deps) =>
                React.useEffect(() => {
                  const prev = __registeringForId;
                  __registeringForId = idRef.current;
                  try {
                    const cleanup = eff();
                    // ensure cleanup runs under the same owner id
                    if (typeof cleanup === "function") {
                      return () => {
                        const p2 = __registeringForId;
                        __registeringForId = idRef.current;
                        try {
                          cleanup();
                        } finally {
                          __registeringForId = p2;
                        }
                      };
                    }
                    return cleanup;
                  } finally {
                    __registeringForId = prev;
                  }
                }, deps);
            }
            return Reflect.get(target, prop, receiver);
          },
        });
        // 3) Evaluate factory under this component's id (captures top-level adds)
        const prev = __registeringForId;
        __registeringForId = idRef.current;
        let Component;
        try {
          Component = factory(scopedReact);
        } finally {
          __registeringForId = prev;
        }
        setComp(() => Component);
        setStatus("ready");
        onReady?.();
      } catch (err) {
        console.error("Error evaluating dynamic component code:", err);
        fail("compile-error: " + (err?.message || String(err)));
      }
    };

    /* schedule compile during idle */
    let cleanupSchedule = null;
    if ("requestIdleCallback" in window) {
      const token = window.requestIdleCallback(compile, { timeout: 1500 });
      cleanupSchedule = () => window.cancelIdleCallback?.(token);
    } else {
      const t = setTimeout(compile, 0);
      cleanupSchedule = () => clearTimeout(t);
    }

    /* watchdog: if still not ready after maxWaitMs → fail */
    timeoutRef.current = setTimeout(() => {
      if (!Comp) fail("timeout");
    }, maxWaitMs);

    return () => {
      cleanupSchedule && cleanupSchedule();
      clearTimeout(timeoutRef.current);
    };
  }, [source, visible, Comp, status, maxWaitMs, onReady, onFail]);

  const adminFailure = (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: "error.main",
        borderRadius: 1,
        backgroundColor: "rgba(244,67,54,0.1)",
      }}
    >
      <Typography variant="body2" sx={{ color: "error.main" }}>
        Dynamic component failed to render. (admin view)
      </Typography>
    </Box>
  );

  return (
    <div
      ref={rootRef}
      onPointerDownCapture={() => {
        // Activate this component to receive global keyboard listeners
        __activeId = idRef.current;
      }}
      onFocusCapture={() => {
        __activeId = idRef.current;
      }}
      style={{
        contentVisibility: "auto",
        containIntrinsicSize: `0 ${heightHint}px`,
      }}
    >
      {status === "failed" ? (
        isAdmin ? (
          adminFailure
        ) : null
      ) : Comp ? (
        <DCErrorBoundary
          onError={(err) => {
            if (!notifiedFail.current) {
              notifiedFail.current = true;
              setStatus("failed");
              onFail?.("runtime-error: " + (err?.message || String(err)));
            }
          }}
          fallback={isAdmin ? adminFailure : null}
        >
          <Comp {...props} />
        </DCErrorBoundary>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </div>
  );
}
